import React, { useState, useEffect } from 'react';
import { TextField, Button, Typography, Box, Grid2 as Grid, Card, CardContent, CircularProgress, Paper } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

const BookingCheckout: React.FC = () => {
  const [timer, setTimer] = useState<number>(300);  // 5 minutes in seconds
  const [bookingConfirmed, setBookingConfirmed] = useState<boolean>(false);
  const [cardDetails, setCardDetails] = useState({ cardNumber: '', expiryDate: '', cvv: '' });
  const [loading, setLoading] = useState(false);
  const [isTimerExpired, setIsTimerExpired] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const { amenity, bookingDate, bookingTime} = location.state || {};

  // Timer countdown logic
  useEffect(() => {
    if (timer === 0) {
      setIsTimerExpired(true);
      navigate('/createBooking');
      return;
    }

    const interval = setInterval(() => {
      setTimer((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [timer]);

  // Handle Credit Card input
  const handleCardChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCardDetails((prevState) => ({ ...prevState, [name]: value }));
  };

  // Handle booking confirmation
  const handleBookingConfirm = async () => {
    if (cardDetails.cardNumber && cardDetails.expiryDate && cardDetails.cvv) {
      setLoading(true);

      // Simulate API request (e.g., Stripe API for payment authorization)
      setTimeout(() => {
        setLoading(false);
        setBookingConfirmed(true);
        navigate('/');
      }, 2000);
    } else {
      alert('Please fill all credit card details.');
    }
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secondsLeft = seconds % 60;
    return `${minutes}:${secondsLeft < 10 ? '0' : ''}${secondsLeft}`;
  };

  return (
    <Paper elevation={5} sx={{ width: '100%', maxWidth: 800, margin: '0 auto', padding: 2 }}>
      <Card variant="outlined">
        <CardContent>
            <Typography variant="h4" gutterBottom>Checkout Summary</Typography>

            {amenity && bookingDate && bookingTime ? (
                <Box sx={{ marginBottom: 2 }}>
                    <Typography variant="h6">Amenity: {amenity}</Typography>
                    <Typography variant="body1">Booking Date: {bookingDate}</Typography>
                    <Typography variant="body1">Booking Time: {bookingTime}</Typography>
                </Box>
            ) : (
                <Typography variant="body1" color="error">
                No booking details found.
                </Typography>
            )}
            <Typography variant="body1" color="textSecondary">
                Please review your booking and authorize the payment. Any damage will be deducted from your security deposit.
            </Typography>

            {isTimerExpired ? (
                <Typography color="error" variant="body1">
                    Time expired! You need to confirm your booking within 5 minutes.
                </Typography>
            ) : (
                <Typography variant="h6" color="primary" gutterBottom>
                    Time remaining: {formatTime(timer)}
                </Typography>
            )}

            <Grid container spacing={2}>
                <Grid size={{xs: 12}}>
                <TextField
                    fullWidth
                    label="Card Number"
                    variant="outlined"
                    name="cardNumber"
                    value={cardDetails.cardNumber}
                    onChange={handleCardChange}
                    disabled={isTimerExpired || bookingConfirmed}
                />
                </Grid>
                <Grid size={{xs: 6}}>
                <TextField
                    fullWidth
                    label="Expiry Date (MM/YY)"
                    variant="outlined"
                    name="expiryDate"
                    value={cardDetails.expiryDate}
                    onChange={handleCardChange}
                    disabled={isTimerExpired || bookingConfirmed}
                />
                </Grid>
                <Grid size={{xs: 6}}>
                <TextField
                    fullWidth
                    label="CVV"
                    variant="outlined"
                    name="cvv"
                    type="password"
                    value={cardDetails.cvv}
                    onChange={handleCardChange}
                    disabled={isTimerExpired || bookingConfirmed}
                />
                </Grid>
            </Grid>

            <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                {loading ? (
                <CircularProgress />
                ) : (
                    <>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleBookingConfirm}
                            disabled={isTimerExpired || bookingConfirmed}
                        >
                            Confirm Booking
                        </Button>
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => navigate('/createBooking')}
                            disabled={isTimerExpired || bookingConfirmed}
                            sx={{ '&:hover': { backgroundColor: '#f44336', color: '#fff' } }}
                        >   
                            Cancel
                        </Button> 
                    </>
                )}
                {bookingConfirmed && (
                <Typography variant="h6" color="green">
                    Booking Confirmed! Your payment has been authorized.
                </Typography>
                )}
            </Box>

            <Box mt={2}>
                <Typography variant="body2" color="error" paragraph>
                ** Please note: Any damage to the property will be deducted from your security deposit.
                </Typography>
            </Box>
        </CardContent>
      </Card>
    </Paper>
  );
};

export default BookingCheckout;
